<template>
  <div class="offsite-page offsite-2025-page">

    <!-- Hero -->
    <section class="offsite-hero">
      <div class="hero-video-bg">
        <iframe
          src="https://player.vimeo.com/video/1115200541?background=1&autoplay=1&loop=1&byline=0&title=0&muted=1"
          allow="autoplay; fullscreen"
          frameborder="0"
        ></iframe>
        <div class="hero-video-overlay"></div>
      </div>
      <div class="hero-content">
        <img
          class="hero-logo"
          src="https://ore.design/wp-content/uploads/2025/09/Logo-lock-up.svg"
          alt="ORE OFFSITE"
        />
        <p class="hero-location">New Orleans 2025</p>
      </div>
    </section>

    <!-- Description -->
    <section class="offsite-description">
      <div class="description-inner">
        <p>ORE OFFSITE 2025 was all about bringing people together at the intersection of landscape, design, and culture.</p>
        <p>This year, Merchant Hall in New Orleans was transformed into an immersive environment — a place where our latest architectural elements met music, art, and community. Guests explored a new installation that celebrated material innovation and the art of placemaking, while conversations, food, and cocktails flowed late into the night.</p>
        <p>It was more than a party. It was a moment to celebrate design, spark new ideas, and connect with the people shaping our industry.</p>
      </div>
    </section>

    <!-- Hero Photo -->
    <section class="offsite-hero-photo">
      <img
        src="https://ore.design/wp-content/uploads/2025/10/ORE_Design-OFFSITE-Website-1-1025-1024x633.jpg"
        alt="ORE OFFSITE 2025 event"
      />
    </section>

    <!-- Gallery Carousel -->
    <section class="offsite-gallery">
      <h2 class="gallery-headline">Did you miss the party?</h2>
      <p class="gallery-subheadline">Relive The Vibe We Had!</p>
      <div class="carousel-wrapper" ref="carouselWrapperRef">
        <button class="carousel-btn carousel-btn--prev" @click="prevSlide" :disabled="carouselIndex === 0" aria-label="Previous">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <div class="carousel-viewport">
          <div class="carousel-track" :style="{ '--ci': carouselIndex }">
            <div
              v-for="(img, i) in carouselImages"
              :key="i"
              class="carousel-slide"
            >
              <img :src="img" :alt="`OFFSITE 2025 photo ${i + 1}`" loading="lazy" />
            </div>
          </div>
        </div>
        <button class="carousel-btn carousel-btn--next" @click="nextSlide" :disabled="carouselIndex >= carouselImages.length - visibleSlides" aria-label="Next">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg>
        </button>
      </div>
      <a href="https://ore.design/offsite/gallery/" class="gallery-cta">Go to the Gallery</a>
    </section>

    <!-- Playlist -->
    <section class="offsite-playlist">
      <img
        class="playlist-cover"
        src="https://ore.design/wp-content/uploads/2025/10/ORE_Design-OFFSITE-Apple_Music-Cover-0925-1-1024x1024.jpg"
        alt="ORE OFFSITE 2025 Playlist"
      />
      <div class="playlist-content">
        <p class="playlist-text">Get the official ORE OFFSITE 2025 playlist on your favorite music platforms!</p>
        <div class="music-links">
          <a
            v-for="platform in musicLinks"
            :key="platform.name"
            :href="platform.url"
            target="_blank"
            rel="noopener"
            :aria-label="platform.name"
            class="music-link"
          >
            <img :src="platform.icon" :alt="platform.name" />
          </a>
        </div>
      </div>
    </section>

    <!-- Email Signup -->
    <section class="offsite-signup">
      <div class="signup-inner">
        <p class="signup-heading">Want more? We'll be sharing<br>photos + video highlights from OFFSITE soon.</p>
        <p class="signup-sub">Sign up to get alerted when they drop, and stay in the loop with future ORE updates.</p>
        <form class="signup-form" @submit.prevent="submitEmail">
          <input
            v-model="emailInput"
            type="email"
            placeholder="Email"
            required
            class="signup-input"
            :disabled="formState === 'submitting'"
          />
          <button type="submit" class="signup-btn" :disabled="formState === 'submitting'">
            {{ formState === 'submitting' ? '...' : 'Submit' }}
          </button>
        </form>
        <p v-if="formState === 'success'" class="signup-success">You're in! We'll be in touch.</p>
        <p v-if="formState === 'error'" class="signup-error">Something went wrong. Please try again.</p>
      </div>
    </section>

    <!-- Previous OFFSITES -->
    <section class="offsite-previous">
      <h2 class="previous-heading">Previous OFFSITES</h2>
      <div class="previous-grid">
        <a
          v-for="event in previousOffsites"
          :key="event.year"
          :href="event.link"
          class="previous-card"
        >
          <img :src="event.img" :alt="`OFFSITE ${event.year} – ${event.city}`" loading="lazy" />
          <div class="previous-card-label">
            <span class="previous-city">{{ event.city }}</span>
            <span class="previous-year">{{ event.year }}</span>
          </div>
        </a>
      </div>
    </section>

    <!-- Footer -->
    <footer class="offsite-footer">
      <p>Copyright © 2025 – All Rights Reserved</p>
    </footer>

  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import Axios from 'axios'

// ── Body class (drives the header/footer overrides at the bottom of this file) ──
onMounted(() => document.body.classList.add('page-offsite-2025'))
onBeforeUnmount(() => document.body.classList.remove('page-offsite-2025'))

// ── Carousel ─────────────────────────────────────────────────────────────────
const carouselImages = [
  'https://ore.design/wp-content/uploads/2025/10/ORE_Design-OFFSITE-Website-Carousel-1-1025.jpg',
  'https://ore.design/wp-content/uploads/2025/10/ORE_Design-OFFSITE-Website-Carousel-2-1025.jpg',
  'https://ore.design/wp-content/uploads/2025/10/ORE_Design-OFFSITE-Website-Carousel-3-1025.jpg',
  'https://ore.design/wp-content/uploads/2025/10/ORE_Design-OFFSITE-Website-Carousel-4-1025-1200x800.jpg',
  'https://ore.design/wp-content/uploads/2025/10/ORE_Design-OFFSITE-Website-Carousel-5-1025.jpg',
  'https://ore.design/wp-content/uploads/2025/10/ORE_Design-OFFSITE-Website-Carousel-6-1025.jpg',
  'https://ore.design/wp-content/uploads/2025/10/ORE_Design-OFFSITE-Website-Carousel-8-1025.jpg',
]
const visibleSlides = 3
const carouselIndex = ref(0)
const carouselWrapperRef = ref(null)

const prevSlide = () => { carouselIndex.value = Math.max(0, carouselIndex.value - 1) }
const nextSlide = () => { carouselIndex.value = Math.min(carouselImages.length - visibleSlides, carouselIndex.value + 1) }

// ── Music platforms ───────────────────────────────────────────────────────────
const musicLinks = [
  {
    name: 'Spotify',
    url: 'https://open.spotify.com/playlist/3Kfsr6AgneqGKjq6gKoY7u?si=zpNoamH2RLO8zbNWdDSspg',
    icon: 'https://ore.design/wp-content/uploads/2025/10/Spotify_icon2.svg',
  },
  {
    name: 'Apple Music',
    url: 'https://music.apple.com/us/playlist/ore-offsite-new-orleans-2025/pl.u-NPK3Cp7LLKD',
    icon: 'https://ore.design/wp-content/uploads/2025/10/Apple_Music_icon2.svg',
  },
  {
    name: 'Amazon Music',
    url: 'https://music.amazon.com/user-playlists/7a1e2a5f708d4b5f87c323de164d9779sune?ref=dm_sh_fzd47iQ2SEBhqaKP1oD3Zp2p4',
    icon: 'https://ore.design/wp-content/uploads/2025/10/Amazon_Music_logo2.svg',
  },
  {
    name: 'YouTube Music',
    url: 'https://music.youtube.com/playlist?list=PLYFT_8JfNYfbC9hx42XFSXSwpIreFFKVv',
    icon: 'https://ore.design/wp-content/uploads/2025/10/Youtube_Music_icon2.svg',
  },
]

// ── Previous OFFSITES ────────────────────────────────────────────────────────
const previousOffsites = [
  { year: '2016', city: 'New Orleans', link: 'https://ore.design/offsite-2016/', img: 'https://ore.design/wp-content/uploads/2025/09/New-Orleans-300x300.jpg' },
  { year: '2017', city: 'Los Angeles',  link: 'https://ore.design/offsite-2017/', img: 'https://ore.design/wp-content/uploads/2025/08/a210b0a2ad22e99537b396f83772a379-300x300.png' },
  { year: '2018', city: 'Philadelphia', link: 'https://ore.design/offsite-2018/', img: 'https://ore.design/wp-content/uploads/2025/08/9ba2f899733ed03fa7d4c1124c3faabe-298x300.png' },
  { year: '2019', city: 'San Diego',    link: 'https://ore.design/offsite-2019/', img: 'https://ore.design/wp-content/uploads/2025/08/4354c47457dc422dab204c4e24781ec1-300x300.png' },
  { year: '2024', city: 'Washington DC', link: 'https://ore.design/offsite-2024/', img: 'https://ore.design/wp-content/uploads/2025/08/Offsite-2024-1-300x300.png' },
]

// ── Email form ───────────────────────────────────────────────────────────────
const emailInput = ref('')
const formState = ref('idle') // idle | submitting | success | error

const submitEmail = async () => {
  if (!emailInput.value) return
  formState.value = 'submitting'
  try {
    const data = new FormData()
    data.append('action', 'elementor_pro_forms_send_form')
    data.append('post_id', '12375')
    data.append('form_id', 'e5c6419')
    data.append('referer_title', 'ORE OFFSITE')
    data.append('form_fields[name]', emailInput.value)
    await Axios.post('/wp-admin/admin-ajax.php', data)
    formState.value = 'success'
    emailInput.value = ''
  } catch {
    formState.value = 'error'
  }
}
</script>

<style lang="scss" scoped>
$black: #000;
$white: #fff;
$orange: #FF4C00;
$green: #224946;
$green-dark: #001610;

.offsite-page {
  background: $black;
  color: $white;
  font-family: 'FHOscar', sans-serif;
  min-height: 100vh;
}

// ── Hero ─────────────────────────────────────────────────────────────────────
.offsite-hero {
  position: relative;
  min-height: 85vh;
  background: $black;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  .hero-video-bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    background: $black;

    iframe {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 177.78vh; // 16:9 aspect
      height: 100%;
      min-width: 100%;
      min-height: 56.25vw; // 16:9 aspect
      transform: translate(-50%, -50%);
      pointer-events: none;
      z-index: 1;
    }

    // Two separate overlays — one for general darkening, one for edge fades
    // Both need explicit z-index above the iframe to prevent compositing layer bleed
    .hero-video-overlay {
      position: absolute;
      inset: 0;
      z-index: 2;
      background-color: rgba(0, 0, 0, 0.72);
    }

    .hero-video-overlay::after {
      content: '';
      position: absolute;
      inset: 0;
      background:
        linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 22%),
        linear-gradient(to left,  rgba(0,0,0,1) 0%, rgba(0,0,0,0) 22%);
    }
  }

  .hero-content {
    position: relative;
    z-index: 1;
    @apply flex flex-col items-center justify-center text-center;
    padding: 120px 24px 80px;
    width: 100%;
  }

  .hero-logo {
    max-width: min(600px, 85vw);
    width: 100%;
    margin-bottom: 40px;
  }

  .hero-location {
    @apply font-FHOscar font-light;
    font-size: clamp(1.5rem, 4vw, 2.5rem);
    color: $white;
    letter-spacing: 0.05em;
    opacity: 0.85;
  }
}

// ── Description ──────────────────────────────────────────────────────────────
.offsite-description {
  @apply flex justify-center;
  background: $white;
  padding: 64px 24px;

  .description-inner {
    max-width: 720px;
    width: 100%;

    p {
      @apply font-FHOscar font-normal;
      font-size: clamp(1rem, 1.8vw, 1.125rem);
      line-height: 1.8;
      color: $green-dark;

      & + p {
        margin-top: 20px;
      }
    }
  }
}

// ── Hero Photo ───────────────────────────────────────────────────────────────
.offsite-hero-photo {
  width: 100%;
  line-height: 0;

  img {
    width: 100%;
    max-height: 70vh;
    object-fit: cover;
    display: block;
  }
}

// ── Gallery ──────────────────────────────────────────────────────────────────
.offsite-gallery {
  @apply flex flex-col items-center;
  background: $black;
  padding: 80px 0 64px;

  .gallery-headline {
    @apply font-FHOscar font-medium;
    font-size: clamp(2.5rem, 6vw, 5rem);
    color: $orange;
    text-align: center;
    line-height: 1.1;
    margin-bottom: 8px;
  }

  .gallery-subheadline {
    @apply font-FHOscar font-light;
    font-size: clamp(1.25rem, 3vw, 2rem);
    color: $white;
    text-align: center;
    margin-bottom: 40px;
  }
}

.carousel-wrapper {
  position: relative;
  width: 100%;
  max-width: 1200px;
  padding: 0 64px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 48px;
  }
}

.carousel-viewport {
  overflow: hidden;
  width: 100%;
}

.carousel-track {
  display: flex;
  // 7 images / 3 visible = track is 233.33% of container
  // translateX by -1/7 of track per step = -1/3 of container per step
  transform: translateX(calc(var(--ci, 0) * -100% / 7));
  transition: transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  width: calc(7 * 100% / 3);

  @media (max-width: 768px) {
    width: calc(7 * 100%);
    transform: translateX(calc(var(--ci, 0) * -100% / 7));
  }
}

.carousel-slide {
  width: calc(100% / 7); // 1/7 of track = 1/3 of viewport
  padding: 0 6px;
  box-sizing: border-box;

  img {
    width: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    display: block;
    border-radius: 12px;
  }
}

.carousel-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: $white;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.25);
  }

  &:disabled {
    opacity: 0.25;
    cursor: default;
  }

  &--prev { left: 8px; }
  &--next { right: 8px; }
}

.gallery-cta {
  display: inline-block;
  margin-top: 40px;
  background: $orange;
  color: $white;
  font-family: 'FHOscar', sans-serif;
  font-weight: 500;
  font-size: 1rem;
  padding: 14px 36px;
  border-radius: 100px;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover { opacity: 0.85; }
}

// ── Playlist ─────────────────────────────────────────────────────────────────
.offsite-playlist {
  @apply flex flex-col md:flex-row items-center justify-center;
  background: $black;
  padding: 80px 24px;
  gap: 40px;
  max-width: 900px;
  margin: 0 auto;

  .playlist-cover {
    width: min(200px, 50vw);
    height: auto;
    border-radius: 12px;
    flex-shrink: 0;
  }

  .playlist-content {
    @apply flex flex-col gap-6;
  }

  .playlist-text {
    @apply font-FHOscar font-normal;
    font-size: clamp(1rem, 2vw, 1.25rem);
    color: $white;
    line-height: 1.6;
  }

  .music-links {
    display: flex;
    gap: 20px;
    align-items: center;
    flex-wrap: wrap;
  }

  .music-link {
    display: flex;
    align-items: center;
    transition: opacity 0.2s, transform 0.2s;

    &:hover {
      opacity: 0.75;
      transform: scale(1.1);
    }

    img {
      width: 40px;
      height: 40px;
      object-fit: contain;
    }
  }
}

// ── Email Signup ──────────────────────────────────────────────────────────────
.offsite-signup {
  @apply flex justify-center;
  background: $black;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 80px 24px;

  .signup-inner {
    @apply flex flex-col items-center text-center;
    max-width: 600px;
    gap: 16px;
  }

  .signup-heading {
    @apply font-FHOscar font-medium;
    font-size: clamp(1.25rem, 3vw, 1.75rem);
    color: $white;
    line-height: 1.4;
  }

  .signup-sub {
    @apply font-FHOscar font-normal;
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.65);
    line-height: 1.6;
  }

  .signup-form {
    @apply flex gap-0 w-full mt-4;
    max-width: 480px;

    .signup-input {
      flex: 1;
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-right: none;
      color: $white;
      font-family: 'FHOscar', sans-serif;
      font-size: 1rem;
      padding: 14px 20px;
      border-radius: 100px 0 0 100px;
      outline: none;

      &::placeholder { color: rgba(255, 255, 255, 0.4); }
      &:focus { border-color: rgba(255, 255, 255, 0.4); }
    }

    .signup-btn {
      background: $orange;
      color: $white;
      font-family: 'FHOscar', sans-serif;
      font-weight: 500;
      font-size: 1rem;
      padding: 14px 28px;
      border-radius: 0 100px 100px 0;
      border: none;
      cursor: pointer;
      transition: opacity 0.2s;
      white-space: nowrap;

      &:hover:not(:disabled) { opacity: 0.85; }
      &:disabled { opacity: 0.5; cursor: default; }
    }
  }

  .signup-success {
    color: #4ade80;
    font-size: 0.9rem;
    margin-top: 8px;
  }

  .signup-error {
    color: #f87171;
    font-size: 0.9rem;
    margin-top: 8px;
  }
}

// ── Previous OFFSITES ─────────────────────────────────────────────────────────
.offsite-previous {
  background: $black;
  padding: 80px 24px;

  .previous-heading {
    @apply font-FHOscar font-medium;
    font-size: clamp(1.75rem, 4vw, 2.5rem);
    color: $white;
    text-align: center;
    margin-bottom: 48px;
    letter-spacing: 0.02em;
  }

  .previous-grid {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    justify-content: center;
    gap: 20px;
    max-width: 1100px;
    margin: 0 auto;

    @media (max-width: 768px) {
      grid-auto-flow: row;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
    @media (max-width: 480px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .previous-card {
    position: relative;
    width: 100%;
    max-width: 200px;
    justify-self: center;
    text-decoration: none;
    border-radius: 12px;
    overflow: hidden;
    transition: transform 0.3s;

    &:hover {
      transform: translateY(-4px);

      .previous-card-label { opacity: 1; }
    }

    img {
      width: 100%;
      aspect-ratio: 1;
      object-fit: cover;
      display: block;
      filter: grayscale(20%);
      transition: filter 0.3s;
    }

    &:hover img { filter: grayscale(0%); }

    .previous-card-label {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: linear-gradient(transparent, rgba(0,0,0,0.8));
      padding: 24px 12px 12px;
      display: flex;
      flex-direction: column;
      gap: 2px;
      opacity: 0.85;
      transition: opacity 0.3s;
    }

    .previous-city {
      font-family: 'FHOscar', sans-serif;
      font-weight: 500;
      font-size: 0.875rem;
      color: $white;
      line-height: 1.3;
    }

    .previous-year {
      font-family: 'FHOscar', sans-serif;
      font-weight: 300;
      font-size: 0.75rem;
      color: rgba(255, 255, 255, 0.7);
    }
  }
}

// ── Footer ───────────────────────────────────────────────────────────────────
.offsite-footer {
  background: $green-dark;
  text-align: center;
  padding: 32px 24px;

  p {
    font-family: 'FHOscar', sans-serif;
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.5);
  }
}
</style>

<style lang="scss">
.page-offsite-2025 .main-footer {
  display: none;
}

// Nav text white over dark hero (only when not scrolled / not on white background)
.page-offsite-2025 .main-header:not(.is-scrolled) {
  .main-header__nav-list a {
    color: #fff !important;
  }
  .main-header__logo svg path,
  .main-header__logo svg rect,
  .main-header__logo svg polygon {
    fill: #fff !important;
  }
}
</style>
